<template>
  <div class="calendar-pricing-editor">
    <div class="well" v-if="!canLoadRates">
      {{ _('Save the product first to edit daily rates and restrictions.') }}
    </div>
    <PricingCalendarShell
      v-else
      ref="shell"
      v-model:view="view"
      v-model:month="month"
      v-model:rangeFrom="rangeFrom"
      v-model:rangeTo="rangeTo"
      v-model:rangeDays="rangeDays"
      :views="views"
      :cellClass="dayCellClass"
      fieldPrefix="calendar"
      v-on:opening="dialogOpening"
    >
      <template #picker>
        <RateplanPicker
          inputId="calendar-rate-plan"
          :plans="plans"
          :selectedId="selectedPlan ? selectedPlan.id : null"
          :optionLabel="planTitle"
          :summary="selectedPlanSummary"
          v-on:select="selectedPlanId = $event"
          v-on:set-default="selectedPlan && setDefault(selectedPlan)"
          v-on:edit="selectedPlan && editPlan(selectedPlan)"
          v-on:add="addPlan"
        />
      </template>

      <template #notice>
        <p class="help-block derived-plan-hint" v-if="isSelectedDerived">{{ derivedPlanHint }}</p>
        <p class="text-success applied-hint" v-if="appliedHint">{{ appliedHint }}</p>
      </template>

      <template #weekly>
        <p class="help-block">{{ isSelectedDerived ? _('Values set here replace the values of the followed plan on that weekday.') : _('Click a weekday to set its rate and restrictions, or drag across several to change them at once. "Every day" applies where a weekday has no value of its own.') }}</p>
        <div class="table-responsive">
          <table class="table table-bordered table-condensed calendar-weekly-grid">
            <thead>
              <tr>
                <th
                  v-for="(col, ci) in weeklyColumns"
                  :key="'cw-' + col.key"
                  :class="{ 'every-col': col.key === '0', 'weekly-head': true }"
                  v-on:pointerdown="weekPointerDown(ci, $event)"
                  v-on:pointerenter="weekPointerEnter(ci)"
                >{{ col.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  v-for="(col, ci) in weeklyColumns"
                  :key="'cwd-' + col.key"
                  :class="weeklyCellClass(col.key) + (isWeekCellSelected(ci) ? ' weekly-selected' : '')"
                  :title="weeklyCellTitle(col.key)"
                  tabindex="0"
                  v-on:pointerdown="weekPointerDown(ci, $event)"
                  v-on:pointerenter="weekPointerEnter(ci)"
                  v-on:keydown.enter="editWeekday(col.key)"
                >
                  <div class="weekly-label">{{ col.label }}</div>
                  <div class="weekly-content">
                    <div :class="'ac-slot ' + weeklyChipClass(col.key)">
                      <i v-if="weeklyCell(col.key).values.stop_sell" class="fa fa-ban text-danger"></i>
                      <span class="ac-price"><i v-if="isPerPerson" class="fa fa-user ac-person"></i><template v-if="weeklyCell(col.key).values.rate">{{ formatRate(weeklyCell(col.key).values.rate) }}<span class="ac-cur">&nbsp;{{ currencyAddon }}</span></template><template v-else>–</template></span>
                    </div>
                    <div class="night-marks" v-if="hasStayLimits(weeklyCell(col.key).values) || weeklyCell(col.key).values.closed_to_arrival || weeklyCell(col.key).values.closed_to_departure">
                      <span v-if="stayNights(weeklyCell(col.key).values, 'min_stay_arrival')" :title="_('Min stay on arrival')">{{ stayNights(weeklyCell(col.key).values, 'min_stay_arrival') }}+</span>
                      <span v-if="stayNights(weeklyCell(col.key).values, 'min_stay_through')" :title="_('Min stay through')">T{{ stayNights(weeklyCell(col.key).values, 'min_stay_through') }}</span>
                      <span v-if="stayNights(weeklyCell(col.key).values, 'max_stay')" :title="_('Max stay')">≤{{ stayNights(weeklyCell(col.key).values, 'max_stay') }}</span>
                      <i v-if="weeklyCell(col.key).values.closed_to_arrival" class="fa fa-sign-in text-info" :title="_('Check-in blocked')"></i>
                      <i v-if="weeklyCell(col.key).values.closed_to_departure" class="fa fa-sign-out text-warning" :title="_('Check-out blocked')"></i>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="small text-muted calendar-legend">
          <span><span class="ac-legend-swatch ac-own"></span> {{ _('Set for the weekday') }}</span>
          <span><span class="ac-legend-swatch ac-inherited"></span> {{ _('From "Every day"') }}</span>
          <span><span class="ac-legend-swatch ac-noprice"></span> {{ _('No price') }}</span>
          <span><span class="ac-legend-swatch ac-closed"></span> <i class="fa fa-ban text-danger"></i> {{ _('Stop sales') }}</span>
          <span v-if="isPerPerson"><i class="fa fa-user ac-person"></i> {{ _('Per person') }}</span>
          <span><span>3+</span> {{ _('Min stay on arrival') }}</span>
        </p>
      </template>

      <template #day="{ date }">
        <template v-if="days[date]">
          <div :class="'ac-slot ' + nightChipClass(days[date])" :title="nightTitle(days[date])">
            <i v-if="days[date].stop_sell" class="fa fa-ban text-danger"></i>
            <span class="ac-price"><i v-if="isPerPerson" class="fa fa-user ac-person"></i><template v-if="days[date].rate">{{ formatRate(days[date].rate) }}<span class="ac-cur">&nbsp;{{ currencyAddon }}</span><span v-if="hasOccupancyRates(days[date])" class="ac-more">+</span></template><template v-else>–</template></span>
          </div>
          <div class="night-marks" v-if="hasStayLimits(days[date]) || days[date].closed_to_arrival || days[date].closed_to_departure">
            <span v-if="stayNights(days[date], 'min_stay_arrival')" :title="_('Min stay on arrival')">{{ stayNights(days[date], 'min_stay_arrival') }}+</span>
            <span v-if="stayNights(days[date], 'min_stay_through')" :title="_('Min stay through')">T{{ stayNights(days[date], 'min_stay_through') }}</span>
            <span v-if="stayNights(days[date], 'max_stay')" :title="_('Max stay')">≤{{ stayNights(days[date], 'max_stay') }}</span>
            <i v-if="days[date].closed_to_arrival" class="fa fa-sign-in text-info" :title="_('Check-in blocked')"></i>
            <i v-if="days[date].closed_to_departure" class="fa fa-sign-out text-warning" :title="_('Check-out blocked')"></i>
          </div>
        </template>
      </template>

      <template #legend>
        <p class="small text-muted calendar-legend">
          <span><span class="ac-legend-swatch ac-default"></span> {{ _('Weekly price') }}</span>
          <span><span class="ac-legend-swatch ac-override"></span> <span class="legend-dot"></span> {{ _('Set for the date') }}</span>
          <span v-if="isSelectedDerived"><span class="ac-legend-swatch ac-default"></span> <i>12</i> {{ _('Calculated from the followed rate') }}</span>
          <span><span class="ac-legend-swatch ac-noprice"></span> {{ _('No price') }}</span>
          <span v-if="isPerPerson"><i class="fa fa-user ac-person"></i> {{ _('Per person') }}</span>
          <span><span>3+</span> {{ _('Min stay on arrival') }}</span>
          <span><span>T2</span> {{ _('Min stay through') }}</span>
          <span><span>≤7</span> {{ _('Max stay') }}</span>
          <span><span class="ac-legend-swatch ac-closed"></span> <i class="fa fa-ban text-danger"></i> {{ _('Stop sales') }}</span>
          <span><i class="fa fa-sign-in text-info"></i> {{ _('Check-in blocked') }}</span>
          <span><i class="fa fa-sign-out text-warning"></i> {{ _('Check-out blocked') }}</span>
        </p>
      </template>

      <template #form>
        <p class="pcs-form-note" v-if="selection.loading"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading the current prices…') }}</p>
        <p class="pcs-form-note" v-else-if="followsWeeklyOnly"><i class="fa fa-fw fa-info-circle"></i> {{ _('These dates follow the weekly prices. What you change here applies to these dates only.') }}</p>
        <NightRateFields :form="form" :currency="currencyAddon" :placeholder="fieldPlaceholder" :emptyLabel="fieldEmptyLabel">
          <template #after-rate>
            <div class="form-group" v-if="selectedPlan && occupancyOptions.length">
              <label>{{ _('Occupancy rates') }}</label>
              <div class="row" v-for="(occ, idx) in occupancyRates" :key="idx">
                <div class="col-xs-6">
                  <input type="number" class="form-control" :placeholder="_('Guests')" v-model.number="occ.occupancy" />
                </div>
                <div class="col-xs-6">
                  <div class="input-group">
                    <input type="number" data-android-inputmode="decimal" step="any" :lang="numberLocale" class="form-control" :placeholder="_('Rate')" v-model="occ.rate" />
                    <span class="input-group-addon">{{ currencyAddon }}</span>
                  </div>
                </div>
              </div>
              <button type="button" class="btn btn-default btn-sm" v-on:click="occupancyRates.push({ occupancy: occupancyRates.length + 1, rate: '' })">{{ _('Add occupancy') }}</button>
            </div>
          </template>
        </NightRateFields>
      </template>

      <template #actions>
        <button type="button" class="btn btn-danger" :disabled="applying || !canRemoveChanges" :title="canRemoveChanges ? _('Return the selected dates to the weekly prices') : _('The selected dates have no changes')" v-on:click="applyRange(true)">
          <i class="fa fa-fw fa-eraser"></i> {{ _('Remove date changes') }}
        </button>
        <button type="button" class="btn btn-success" :disabled="applying" v-on:click="applyRange(false)">
          <i class="fa fa-fw fa-check"></i> {{ _('Apply to selected dates') }}
        </button>
      </template>
    </PricingCalendarShell>
    <PricingDialog ref="weekDialog" :title="weeklyTitle" v-on:closed="weeklyClosed">
      <div class="pcs-inline-field">
        <label>{{ _('Weekdays') }}</label>
        <div class="pcs-toggles" role="group">
          <button
            v-for="col in weeklyColumns"
            :key="'wwd-' + col.key"
            type="button"
            :class="'btn btn-sm ' + (weekly.keys.includes(col.key) ? 'btn-success' : 'btn-default')"
            :aria-pressed="weekly.keys.includes(col.key) ? 'true' : 'false'"
            v-on:click="toggleWeeklyKey(col.key)"
          >{{ col.label }}</button>
        </div>
      </div>
      <p class="pcs-form-note" v-if="weeklyNote"><i class="fa fa-fw fa-info-circle"></i> {{ weeklyNote }}</p>
      <NightRateFields :form="weekly.form" :currency="currencyAddon" :placeholder="weeklyPlaceholder" :help="weeklyHelp" :emptyLabel="weeklyEmptyLabel" />
      <template #actions>
        <button type="button" class="btn btn-danger" v-if="weeklyCanClear" :title="_('Remove the values set here, so they follow Every day again')" v-on:click="clearWeekly">
          <i class="fa fa-fw fa-eraser"></i> {{ _('Clear') }}
        </button>
        <button type="button" class="btn btn-success" v-on:click="saveWeekly">
          <i class="fa fa-fw fa-check"></i> {{ _('Save') }}
        </button>
      </template>
    </PricingDialog>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import errors from '~/lib/errors'
import formats from '~/lib/formats'
import modals from '~/lib/modals'
import currencies from '~/lib/currencies'
import constants from '~/lib/constants'
import rateplanhelpers from '~/lib/rateplanhelpers'
import NightRateFields from '~/components/NightRateFields.vue'
import PricingCalendarShell from '~/components/PricingCalendarShell.vue'
import PricingDialog from '~/components/PricingDialog.vue'
import RateplanPicker from '~/components/RateplanPicker.vue'

export default {
  mixins: [errors, formats, modals, currencies, constants, rateplanhelpers],
  components: { NightRateFields, PricingCalendarShell, PricingDialog, RateplanPicker },
  props: {
    product: { type: Object, required: true },
    settings: { type: Object, default: null },
    shopId: { type: [String, Number], default: '' },
    apiKey: { type: String, default: '' },
    baseDomain: { type: String, default: '' },
    canEditRates: { type: Boolean, default: false },
    termsOptions: { type: [Array, Object], default: () => [] },
    withPlanTerms: { type: Boolean, default: false }, // Activity pricing: rate plans have their own terms and cancellation requests
    pricetiers: { type: Array, default: () => [] } // The price tiers the product uses
  },
  data () {
    return {
      plans: [],
      selectedPlan: null,
      days: {},
      month: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      view: 'calendar',
      rangeFrom: '',
      rangeTo: '',
      rangeDays: ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'],
      applying: false,
      appliedHint: '',
      occupancyRates: [],
      prefill: {}, // The values the dialog's fields started from; only fields that differ are saved
      dialogActive: false,
      weekly: { keys: [], states: {}, form: {}, onClosed: null }, // The weekly dialog: its weekdays ("0" every day)
      weekSel: { anchor: null, current: null, dragging: false, open: false }, // Weekdays selected by dragging (column indexes)
      selection: { loading: false, known: false, nights: {}, seq: 0 }, // Current values of the dialog's dates
      fieldStates: {},
      form: {
        rate: '',
        min_stay_arrival: '',
        min_stay_through: '',
        max_stay: '',
        stop_sell: '',
        closed_to_arrival: '',
        closed_to_departure: ''
      },
      formMixed: {
        rate: false,
        min_stay_arrival: false,
        min_stay_through: false,
        max_stay: false,
        stop_sell: false,
        closed_to_arrival: false,
        closed_to_departure: false
      }
    }
  },
  computed: {
    ...mapGetters(['locale', 'version']),
    shortLocale () {
      return this.locale ? this.locale.split('_')[0] : 'en'
    },
    canLoadRates () {
      return !!(this.canEditRates && this.product && this.product.id)
    },
    hasSelection () {
      return !!(this.rangeFrom && this.rangeTo)
    },
    currencyAddon () {
      return this.currencySymbol(this.settings && this.settings.currency ? this.settings.currency : 'EUR')
    },
    tierList () { // In priority order, as the included guests are covered tier by tier
      return (this.pricetiers || []).filter(t => t && t.id)
        .slice()
        .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0) || Number(a.id) - Number(b.id))
        .map(t => ({ id: String(t.id), name: t.name || ('#' + t.id), category: t.category ? String(t.category).toUpperCase() : '' }))
    },
    adultTierId () { // The tier a per person rate is for: the Adult traveller category, or the first tier when no tier has a category
      if (this.tierList.some(t => t.category)) {
        const adult = this.tierList.find(t => t.category === 'ADULT')
        return adult ? adult.id : ''
      }
      return this.tierList.length ? this.tierList[0].id : ''
    },
    selectedPlanId: {
      get () {
        return this.selectedPlan ? this.selectedPlan.id : ''
      },
      set (id) {
        const plan = this.plans.find(p => String(p.id) === String(id))
        if (plan) {
          this.selectPlan(plan)
        }
      }
    },
    stayFields () {
      return [
        { name: 'min_stay_arrival', label: this._('Min stay on arrival'), help: this._('Minimum stay required when the stay starts on this day.') },
        { name: 'min_stay_through', label: this._('Min stay through'), help: this._('Minimum stay required if the stay includes this day.') },
        { name: 'max_stay', label: this._('Max stay'), help: this._('Maximum stay allowed when the stay starts on this day.') }
      ]
    },
    followsWeeklyOnly () {
      const nights = this.selection.known ? this.nightsInSelectedRange() : []
      return !!(nights.length && nights.every(night => night.source !== 'manual' && night.source !== 'override'))
    },
    canRemoveChanges () {
      return !this.selection.known || this.nightsInSelectedRange().some(night => night.source === 'manual' || night.source === 'override')
    },
    weeklyKeysInOrder () {
      return this.weeklyColumns.map(c => c.key).filter(key => this.weekly.keys.includes(key))
    },
    weeklyEveryDay () {
      return this.weekly.keys.length === 1 && this.weekly.keys[0] === '0'
    },
    weeklyTitle () {
      const labels = this.weeklyKeysInOrder.map(key => this.weeklyColumns.find(c => c.key === key).label)
      return labels.length > 3 ? labels[0] + '–' + labels[labels.length - 1] : labels.join(', ')
    },
    weeklyNote () {
      if (this.weeklyEveryDay) {
        return this._('Used on every weekday that has no value of its own.')
      }
      if (this.weekly.keys.length > 1) {
        return this._('%s weekdays selected. The values are saved to all of them; fields you don\'t change stay as they are.').replace('%s', String(this.weekly.keys.length))
      }
      return ''
    },
    weeklyCanClear () {
      return this.weekly.keys.some(key => this.weekdayDefaults[key] && Object.keys(this.weekdayDefaults[key]).length)
    },
    weeklyFieldNames () {
      return ['rate', 'min_stay_arrival', 'min_stay_through', 'max_stay', 'stop_sell', 'closed_to_arrival', 'closed_to_departure']
    },
    isPerPerson () { // The rates are per guest: 1 guest in the base price and the adults at 100 % of the night price
      return this.planIsPerPerson(this.selectedPlan)
    },
    views () {
      return [{ id: 'weekly', label: this._('Weekly prices') }, { id: 'calendar', label: this._('Calendar') }]
    },
    weekdayDefaults () { // rateplan.weekday_defaults: "0" every day, "1"-"7" Mon-Sun
      const raw = this.selectedPlan && this.selectedPlan.weekday_defaults
      if (!raw) {
        return {}
      }
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
      } catch (e) {
        return {}
      }
    },
    weeklyColumns () {
      const labels = [this._('Mon'), this._('Tue'), this._('Wed'), this._('Thu'), this._('Fri'), this._('Sat'), this._('Sun')]
      return labels.map((label, i) => ({ key: String(i + 1), label })).concat([{ key: '0', label: this._('Every day') }])
    },
    selectedPlanSummary () { // Chips under the plan picker: the settings the dropdown label does not show
      const plan = this.selectedPlan
      if (!plan) {
        return []
      }
      const parts = []
      const perPerson = this.planIsPerPerson(plan)
      if (perPerson) {
        parts.push(this._('Price per person'))
      } else if (parseInt(plan.includes) > 0) {
        parts.push(this._('Base price includes %s guests').replace('%s', parseInt(plan.includes)))
      }
      const fees = this.planTierFees(plan)
      const percents = this.planTierPercents(plan)
      this.tierList.forEach(tier => {
        if (perPerson && tier.id === this.adultTierId) {
          return
        }
        if (Number(percents[tier.id])) {
          parts.push(this._('%s / night').replace('%s', tier.name) + ' ' + this.formatRate(percents[tier.id]) + ' %')
        } else if (Number(fees[tier.id])) {
          parts.push(this._('%s / night').replace('%s', tier.name) + ' ' + this.formatRate(fees[tier.id]) + ' ' + this.currencyAddon)
        }
      })
      if (this.withPlanTerms && parseInt(plan.terms_id)) {
        parts.push(this._('Terms') + ': ' + this.rateplanTermsLabel(plan, this.termsOptions))
      }
      if (this.withPlanTerms && plan.allow_cancellation_request !== null && plan.allow_cancellation_request !== undefined && plan.allow_cancellation_request !== '') {
        parts.push(this._('Cancellation requests') + ': ' + this.rateplanCancellationText(plan))
      }
      const extra = this.extraNightSummary(plan)
      if (extra) {
        parts.push(extra)
      }
      return parts
    },
    occupancyOptions () {
      return this.planOptions(this.selectedPlan).filter(opt => opt && opt.occupancy && opt.rate !== undefined && !opt.derived_option)
    },
    isSelectedDerived () {
      return this.isDerivedPlan(this.selectedPlan)
    },
    selectedParentPlan () {
      if (!this.selectedPlan || !this.selectedPlan.parent_rateplan_id) {
        return null
      }
      return this.plans.find(p => String(p.id) === String(this.selectedPlan.parent_rateplan_id)) || null
    },
    derivedPlanHint () {
      if (!this.isSelectedDerived) {
        return ''
      }
      if (!this.selectedParentPlan) {
        return this._('This plan is derived. Choose a parent rate plan to show prices and restrictions.')
      }
      const modifier = this.derivedSummary(this.selectedPlan)
      const parent = this.selectedParentPlan.title
      const follow = this._('Nightly rates and stay restrictions follow %s.').replace(/%s/, parent)
      const calc = this._('Calculated prices are shown in italic. Enter a value below to override selected dates.')
      return modifier ? follow + ' ' + modifier + ' ' + calc : follow + ' ' + calc
    }
  },
  watch: {
    'product.id' () {
      this.reload()
    },
    version () {
      this.reload()
    },
    month () {
      this.loadDays()
    },
    async canLoadRates (ready) {
      if (!ready) {
        return
      }
      await this.reload()
      if (!this.plans.length) {
        await this.ensureDefaultPlan()
        await this.loadDays()
      }
    },
    rangeFrom () {
      this.selectionChanged()
    },
    rangeTo () {
      this.selectionChanged()
    },
    rangeDays: {
      handler () {
        if (this.dialogActive) {
          this.applyPrefill()
        }
      },
      deep: true
    }
  },
  methods: {
    api (path) {
      const versionStr = this.version && this.version !== 'default' ? '&version=' + this.version : ''
      return this.baseDomain + this.shopId + path + (path.includes('?') ? '&' : '?') + 'apikey=' + this.apiKey + versionStr
    },
    toIso (d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return y + '-' + m + '-' + day
    },
    fromIso (iso) {
      if (!iso) {
        return null
      }
      const parts = String(iso).split('-')
      if (parts.length < 3) {
        return null
      }
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10))
    },
    fieldPlaceholder (field) {
      const state = this.fieldStates[field]
      if (state && state.varies) {
        const numbers = state.values.filter(v => v !== '').map(Number)
        if (!numbers.length) {
          return this._('Varies')
        }
        const text = this.formatRate(Math.min(...numbers)) + '–' + this.formatRate(Math.max(...numbers)) + (field === 'rate' ? ' ' + this.currencyAddon : '')
        return this._('Varies: %s').replace('%s', state.values.includes('') ? text + ', ' + this._('not set') : text)
      }
      return this.selection.known ? this._('Not set') : this._('Leave unchanged')
    },
    fieldEmptyLabel (field) {
      const state = this.fieldStates[field]
      return state && state.varies ? this._('Varies') : this._('Leave unchanged')
    },
    dialogOpening () {
      ;['rate', 'min_stay_arrival', 'min_stay_through', 'max_stay', 'stop_sell', 'closed_to_arrival', 'closed_to_departure'].forEach(field => {
        this.form[field] = ''
      })
      this.prefill = {}
      this.occupancyRates = []
      this.dialogActive = true
      this.loadSelection()
    },
    selectionChanged () {
      if (!this.rangeFrom || !this.rangeTo) { // The dialog was closed
        this.dialogActive = false
        return
      }
      if (this.dialogActive) {
        this.loadSelection()
      }
    },
    /**
     * Current values of the dialog's dates: from the shown month, or loaded for up to a year.
     * Longer ranges are not prefilled.
     */
    async loadSelection () {
      if (!this.selectedPlan || !this.rangeFrom || !this.rangeTo) {
        return
      }
      const seq = ++this.selection.seq
      const dates = []
      for (let d = this.fromIso(this.rangeFrom); d <= this.fromIso(this.rangeTo); d.setDate(d.getDate() + 1)) {
        dates.push(this.toIso(d))
      }
      if (dates.every(date => this.days[date])) {
        const nights = {}
        dates.forEach(date => { nights[date] = this.days[date] })
        this.selection = { loading: false, known: true, nights, seq }
        this.applyPrefill()
        return
      }
      if (dates.length > 366) {
        this.selection = { loading: false, known: false, nights: {}, seq }
        this.applyPrefill()
        return
      }
      this.selection = { loading: true, known: false, nights: {}, seq }
      try {
        const response = await axios.get(this.api('/products/' + this.product.id + '/rateplans/' + this.selectedPlan.id + '/restrictions.json?from=' + this.rangeFrom + '&to=' + this.rangeTo))
        if (seq === this.selection.seq) {
          this.selection = { loading: false, known: true, nights: (response.data && response.data.days) || {}, seq }
        }
      } catch (e) {
        if (seq === this.selection.seq) {
          this.selection = { loading: false, known: false, nights: {}, seq }
        }
      }
      if (seq === this.selection.seq) {
        this.applyPrefill()
      }
    },
    /**
     * Fill the fields with the dates' common values; fields whose values differ stay empty and say
     * so. A field the user has changed keeps its value.
     */
    applyPrefill () {
      const nights = this.selection.known ? this.nightsInSelectedRange() : []
      const fields = ['rate', 'min_stay_arrival', 'min_stay_through', 'max_stay', 'stop_sell', 'closed_to_arrival', 'closed_to_departure']
      const states = {}
      const prefill = {}
      fields.forEach(field => {
        const values = Array.from(new Set(nights.map(night => this.normalizeField(night, field))))
        const state = { value: values.length === 1 ? values[0] : '', varies: values.length > 1, values }
        states[field] = state
        const current = this.form[field] === null || this.form[field] === undefined ? '' : String(this.form[field])
        if (current === (this.prefill[field] || '')) {
          this.form[field] = state.value
        }
        prefill[field] = state.value
        this.formMixed[field] = state.varies
      })
      this.fieldStates = states
      this.prefill = prefill
    },
    clearStay (field) {
      this.form[field] = '0'
      this.formMixed[field] = false
    },
    nightsInSelectedRange () {
      if (!this.rangeFrom || !this.rangeTo) {
        return []
      }
      const nights = []
      const from = this.fromIso(this.rangeFrom)
      const to = this.fromIso(this.rangeTo)
      if (!from || !to) {
        return []
      }
      const filterDays = this.rangeDays.length && this.rangeDays.length < 7
      for (let d = new Date(from.getTime()); d <= to; d.setDate(d.getDate() + 1)) {
        const wd = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'][d.getDay()]
        if (filterDays && this.rangeDays.indexOf(wd) === -1) {
          continue
        }
        const iso = this.toIso(d)
        nights.push(this.selection.nights[iso] || this.days[iso] || {})
      }
      return nights
    },
    normalizeField (night, field) {
      if (field === 'rate') {
        const n = Number(night && night.rate)
        return n > 0 ? String(n) : ''
      }
      if (field === 'min_stay_arrival' || field === 'min_stay_through' || field === 'max_stay') {
        const n = this.stayNights(night, field)
        return n ? String(n) : ''
      }
      return night && (night[field] === true || parseInt(night[field], 10) === 1) ? '1' : '0'
    },
    formChanged (field) {
      const value = this.form[field] === null || this.form[field] === undefined ? '' : String(this.form[field])
      return value !== (this.prefill[field] || '')
    },
    isDerivedPlan (plan) {
      return !!(plan && plan.rate_mode === 'derived')
    },
    isDerivedNight (night) {
      return !!(this.isSelectedDerived && night && night.source === 'derived')
    },
    planOptions (plan) {
      if (!plan || !plan.options) {
        return []
      }
      try {
        const parsed = typeof plan.options === 'string' ? JSON.parse(plan.options) : plan.options
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },
    derivedOption (plan) {
      const found = this.planOptions(plan).find(opt => opt && opt.derived_option)
      return found ? found.derived_option : null
    },
    derivedSummary (plan) {
      const opt = this.derivedOption(plan)
      if (!opt) {
        return ''
      }
      const amount = Number(opt.rate)
      if (!amount) {
        return ''
      }
      const labels = {
        increase_by_amount: this._('increased by %s'),
        decrease_by_amount: this._('decreased by %s'),
        increase_by_percent: this._('increased by %s'),
        decrease_by_percent: this._('decreased by %s')
      }
      const label = labels[opt.modifier] || labels.increase_by_amount
      const value = this.formatRate(amount) + (String(opt.modifier).indexOf('percent') !== -1 ? '%' : '')
      return label.replace(/%s/, value)
    },
    defaultParentId (plan) {
      if (plan && plan.parent_rateplan_id) {
        return plan.parent_rateplan_id
      }
      const other = this.plans.find(p => !plan || String(p.id) !== String(plan.id))
      return other ? other.id : ''
    },
    planTitle (plan) { // The guest pricing is in the summary chips; a derived plan also tells what it follows
      const bits = []
      if (this.isDerivedPlan(plan)) {
        const parent = this.plans.find(p => String(p.id) === String(plan.parent_rateplan_id))
        if (parent) {
          bits.push(this._('from %s').replace(/%s/, parent.title))
        }
        const modifier = this.derivedSummary(plan)
        if (modifier) {
          bits.push(modifier)
        }
      }
      return (parseInt(plan.is_default) ? '★ ' : '') + plan.title + (bits.length ? ' (' + bits.join(', ') + ')' : '')
    },
    formatRate (rate) {
      const n = Number(rate)
      if (!n) {
        return ''
      }
      return n % 1 ? n.toFixed(2) : String(n)
    },
    stayNights (night, field) {
      const n = parseInt(night && night[field], 10)
      return n > 0 ? n : 0
    },
    hasStayLimits (night) {
      return !!(this.stayNights(night, 'min_stay_arrival') || this.stayNights(night, 'min_stay_through') || this.stayNights(night, 'max_stay'))
    },
    dayCellClass (date) {
      const night = this.days[date]
      if (!night) {
        return ''
      }
      const classes = []
      if (night.stop_sell) {
        classes.push('pmg-closed')
      }
      if (night.source === 'manual' || night.source === 'override') {
        classes.push('pmg-changed')
      }
      return classes.join(' ')
    },
    nightChipClass (night) { // As the activity calendar: weekly rates are quiet, rates set for the date stand out
      if (night.stop_sell) {
        return 'ac-closed'
      }
      if (!night.rate) {
        return 'ac-noprice'
      }
      if (this.isDerivedNight(night)) {
        return 'ac-default ac-derived'
      }
      return night.source === 'manual' || night.source === 'override' ? 'ac-override' : 'ac-default'
    },
    weeklyChipClass (key) {
      const cell = this.weeklyCell(key)
      if (cell.values.stop_sell) {
        return cell.inherited.stop_sell ? 'ac-closed ac-inherited-closed' : 'ac-closed'
      }
      if (!cell.values.rate) {
        return 'ac-noprice'
      }
      return cell.inherited.rate ? 'ac-inherited' : 'ac-own'
    },
    hasOccupancyRates (night) { // Different rates by the number of guests
      const rates = Array.isArray(night.rates) ? night.rates.map(r => Number(r.rate)) : []
      return new Set(rates).size > 1
    },
    nightTitle (night) {
      const lines = []
      if (night.rate) {
        lines.push(this._('Rate') + ' ' + this.priceText(night.rate) + (this.isPerPerson ? ' / ' + this._('person') : ''))
      } else {
        lines.push(this._('No price'))
      }
      ;(Array.isArray(night.rates) ? night.rates : []).forEach(r => {
        if (r && r.occupancy) {
          lines.push(this._('%s guests').replace('%s', r.occupancy) + ' ' + this.priceText(r.rate))
        }
      })
      this.stayFields.forEach(f => {
        if (this.stayNights(night, f.name)) {
          lines.push(f.label + ' ' + this.stayNights(night, f.name))
        }
      })
      if (night.stop_sell) {
        lines.push(this._('Stop sales'))
      }
      if (night.closed_to_arrival) {
        lines.push(this._('Check-in blocked'))
      }
      if (night.closed_to_departure) {
        lines.push(this._('Check-out blocked'))
      }
      const sources = { default: this._('Weekly price'), manual: this._('Set for the date'), override: this._('Set for the date'), derived: this._('Calculated from the followed rate') }
      if (sources[night.source]) {
        lines.push(sources[night.source])
      }
      return lines.join('\n')
    },
    priceText (rate) {
      return this.formatRate(rate) + ' ' + this.currencyAddon
    },
    weeklyCell (key) {
      const own = this.weekdayDefaults[key] || {}
      const every = key !== '0' ? (this.weekdayDefaults['0'] || {}) : {}
      const values = {}
      const inherited = {}
      ;['rate', 'min_stay_arrival', 'min_stay_through', 'max_stay', 'stop_sell', 'closed_to_arrival', 'closed_to_departure'].forEach(field => {
        if (own[field] !== undefined && own[field] !== null && own[field] !== '') {
          values[field] = own[field]
        } else if (every[field] !== undefined && every[field] !== null && every[field] !== '') {
          values[field] = every[field]
          inherited[field] = true
        }
      })
      return { own, values, inherited }
    },
    weeklyCellClass (key) {
      const classes = ['weekly-cell']
      if (key === '0') {
        classes.push('every-col')
      } else if (key === '6' || key === '7') {
        classes.push('weekly-weekend')
      }
      if (this.weeklyCell(key).values.stop_sell) {
        classes.push('weekly-closed')
      }
      return classes.join(' ')
    },
    weeklyCellTitle (key) {
      const cell = this.weeklyCell(key)
      const lines = []
      if (cell.values.rate) {
        lines.push(this._('Rate') + ' ' + this.formatRate(cell.values.rate) + ' ' + this.currencyAddon)
      }
      ;[['min_stay_arrival', this._('Min stay on arrival')], ['min_stay_through', this._('Min stay through')], ['max_stay', this._('Max stay')]].forEach(([field, label]) => {
        if (this.stayNights(cell.values, field)) {
          lines.push(label + ' ' + this.stayNights(cell.values, field))
        }
      })
      if (cell.values.stop_sell) {
        lines.push(this._('Stop sales'))
      }
      if (cell.values.closed_to_arrival) {
        lines.push(this._('Check-in blocked'))
      }
      if (cell.values.closed_to_departure) {
        lines.push(this._('Check-out blocked'))
      }
      return lines.length ? lines.join('\n') : this._('No price')
    },
    // --- Selecting weekdays: a click edits one, dragging (or Shift-click) selects several ---
    isWeekCellSelected (ci) {
      const sel = this.weekSel
      if (sel.open) { // The dialog's weekdays
        return this.weekly.keys.includes(this.weeklyColumns[ci].key)
      }
      if (sel.anchor === null || sel.current === null || !sel.dragging) {
        return false
      }
      return ci >= Math.min(sel.anchor, sel.current) && ci <= Math.max(sel.anchor, sel.current)
    },
    weekPointerDown (ci, event) {
      if (event.button !== undefined && event.button !== 0) {
        return
      }
      if (event.pointerType === 'mouse') {
        event.preventDefault()
      }
      const extend = event.shiftKey && this.weekSel.anchor !== null
      this.weekSel = { anchor: extend ? this.weekSel.anchor : ci, current: ci, dragging: true, open: false }
      window.addEventListener('pointerup', this.weekPointerUp, { once: true })
      window.addEventListener('pointercancel', this.weekPointerCancel, { once: true })
    },
    weekPointerEnter (ci) {
      if (this.weekSel.dragging) {
        this.weekSel.current = ci
      }
    },
    weekPointerCancel () {
      window.removeEventListener('pointerup', this.weekPointerUp)
      this.weekSel = { anchor: null, current: null, dragging: false, open: false }
    },
    weekPointerUp () {
      window.removeEventListener('pointercancel', this.weekPointerCancel)
      if (!this.weekSel.dragging) {
        return
      }
      const sel = this.weekSel
      const keys = this.weeklyColumns.slice(Math.min(sel.anchor, sel.current), Math.max(sel.anchor, sel.current) + 1).map(c => c.key)
      this.weekSel = Object.assign({}, sel, { dragging: false, open: true })
      const closed = () => { this.weekSel = Object.assign({}, this.weekSel, { open: false }) }
      if (keys.length === 1) {
        this.editWeekday(keys[0], closed)
      } else {
        this.editWeekdays(keys, closed)
      }
    },
    /**
     * The dialog of one or several weekdays, laid out as the date dialog: its weekdays start from
     * the selection and can be changed. The fields show the values the weekdays have now, including
     * what they take from "Every day" (marked under the field); fields whose values differ say so.
     * Only the fields that are changed are saved, to every weekday.
     */
    editWeekdays (keys, onClosed) {
      if (!this.selectedPlan || !keys.length) {
        return
      }
      this.weekly = { keys: keys.slice(), states: {}, form: {}, onClosed }
      this.weeklyPrefill()
      this.$refs.weekDialog.open()
    },
    toggleWeeklyKey (key) { // A weekday on or off; at least one stays on
      const keys = this.weekly.keys
      if (keys.includes(key)) {
        if (keys.length > 1) {
          this.weekly.keys = keys.filter(k => k !== key)
        }
      } else {
        this.weekly.keys = keys.concat([key])
      }
      this.weeklyPrefill()
    },
    /**
     * Fill the fields with the weekdays' values. A field the user has changed keeps its value when
     * the weekdays change.
     */
    weeklyPrefill () {
      const cells = this.weekly.keys.map(key => this.weeklyCell(key))
      const single = cells.length === 1
      const text = v => v === null || v === undefined || v === '' ? '' : String(v)
      const states = {}
      this.weeklyFieldNames.forEach(name => {
        const values = Array.from(new Set(cells.map(cell => text(cell.values[name]))))
        states[name] = {
          value: values.length === 1 ? values[0] : '',
          varies: values.length > 1,
          inherited: single && !!cells[0].inherited[name]
        }
        const before = this.weekly.states[name] ? this.weekly.states[name].value : ''
        if (text(this.weekly.form[name]) === before) {
          this.weekly.form[name] = states[name].value
        }
      })
      this.weekly.states = states
    },
    weeklyPlaceholder (name) {
      const state = this.weekly.states[name]
      return state && state.varies ? this._('Varies') : this._('Not set')
    },
    weeklyHelp (name) {
      const state = this.weekly.states[name]
      return state && state.inherited ? this._('Taken from %s').replace('%s', this._('Every day')) : ''
    },
    weeklyEmptyLabel (name) { // The empty choice of a flag in the weekly dialog
      const state = this.weekly.states[name]
      if (state && state.varies) {
        return this._('Varies')
      }
      return this.weeklyEveryDay ? this._('Not set') : this._('As every day')
    },
    saveWeekly () {
      const text = v => v === null || v === undefined || v === '' ? '' : String(v)
      const changed = this.weeklyFieldNames.filter(name => text(this.weekly.form[name]) !== this.weekly.states[name].value)
      const keys = this.weekly.keys
      const form = this.weekly.form
      this.$refs.weekDialog.close()
      if (!changed.length) {
        return
      }
      const map = JSON.parse(JSON.stringify(this.weekdayDefaults))
      keys.forEach(key => {
        const entry = Object.assign({}, map[key] || {})
        changed.forEach(name => {
          const value = text(form[name])
          if (value === '') {
            delete entry[name]
          } else if (name === 'rate') {
            entry.rate = this.parseDecimal(value)
          } else {
            entry[name] = Math.max(0, parseInt(value, 10) || 0)
          }
        })
        if (Object.keys(entry).length) {
          map[key] = entry
        } else {
          delete map[key]
        }
      })
      this.saveWeekdayMap(map)
    },
    clearWeekly () {
      const map = JSON.parse(JSON.stringify(this.weekdayDefaults))
      this.weekly.keys.forEach(key => { delete map[key] })
      this.$refs.weekDialog.close()
      this.saveWeekdayMap(map)
    },
    weeklyClosed () {
      if (this.weekly.onClosed) {
        this.weekly.onClosed()
      }
      this.weekly.onClosed = null
    },
    editWeekday (key, onClosed) {
      this.editWeekdays([key], onClosed)
    },
    async saveWeekday (key, entry) {
      const map = JSON.parse(JSON.stringify(this.weekdayDefaults))
      if (entry) {
        map[key] = entry
      } else {
        delete map[key]
      }
      await this.saveWeekdayMap(map)
    },
    async saveWeekdayMap (map) {
      const plan = this.selectedPlan
      if (!plan) {
        return
      }
      const prev = plan.weekday_defaults
      plan.weekday_defaults = JSON.stringify(map)
      try {
        await axios.put(this.api('/products/' + this.product.id + '/rateplans/' + plan.id + '.json'), { weekday_defaults: map })
        this.appliedHint = this._('Rates updated')
        setTimeout(() => { this.appliedHint = '' }, 2000)
      } catch (e) {
        plan.weekday_defaults = prev
        this.showError(e, undefined, true)
      }
      await this.loadPlans(plan.id)
      await this.loadDays()
    },
    extraNightSummary (plan) {
      const amount = this.formatRate(plan.extra_night_amount)
      if (!plan.extra_night_modifier || !amount) {
        return ''
      }
      const labels = {
        decrease_by_amount: this._('Extra nights') + ' −' + amount + ' ' + this.currencyAddon,
        decrease_by_percent: this._('Extra nights') + ' −' + amount + ' %',
        set_rate: this._('Extra nights') + ' ' + amount + ' ' + this.currencyAddon
      }
      return labels[plan.extra_night_modifier] || ''
    },
    async reload () {
      if (!this.canLoadRates) {
        return
      }
      await this.loadPlans()
      await this.loadDays()
    },
    normalizePlan (data) {
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return null
      }
      if (data.id) {
        return data
      }
      if (data.data && data.data.id) {
        return data.data
      }
      if (data.rateplan && data.rateplan.id) {
        return data.rateplan
      }
      return null
    },
    normalizePlans (data) {
      if (Array.isArray(data)) {
        return data.slice()
      }
      if (!data || typeof data !== 'object') {
        return []
      }
      if (Array.isArray(data.data)) {
        return data.data.slice()
      }
      if (Array.isArray(data.rateplans)) {
        return data.rateplans.slice()
      }
      const values = Object.keys(data).every(key => /^\d+$/.test(key))
        ? Object.keys(data).sort((a, b) => Number(a) - Number(b)).map(key => data[key])
        : []
      return values.filter(item => item && typeof item === 'object' && item.id)
    },
    async loadPlans (selectId) {
      const response = await axios.get(this.api('/products/' + this.product.id + '/rateplans.json') + '&_=' + Date.now())
      this.plans = this.normalizePlans(response.data)
      if (selectId) {
        const created = this.plans.find(p => String(p.id) === String(selectId))
        if (created) {
          this.selectedPlan = created
          return
        }
      }
      if (!this.selectedPlan && this.plans.length) {
        this.selectedPlan = this.plans.find(p => parseInt(p.is_default)) || this.plans[0]
      } else if (this.selectedPlan) {
        this.selectedPlan = this.plans.find(p => String(p.id) === String(this.selectedPlan.id)) || this.selectedPlan
      }
    },
    async loadDays () {
      if (!this.selectedPlan) {
        this.days = {}
        return
      }
      const from = this.toIso(new Date(this.month.getFullYear(), this.month.getMonth(), 1))
      const to = this.toIso(new Date(this.month.getFullYear(), this.month.getMonth() + 1, 0))
      const response = await axios.get(this.api('/products/' + this.product.id + '/rateplans/' + this.selectedPlan.id + '/restrictions.json?from=' + from + '&to=' + to))
      this.days = (response.data && response.data.days) ? response.data.days : {}
    },
    selectPlan (plan) {
      this.selectedPlan = plan
      this.loadDays()
    },
    async ensureDefaultPlan () {
      if (this.plans.length) {
        return
      }
      await axios.post(this.api('/products/' + this.product.id + '/rateplans.json'), {
        title: this.product.name || this._('Standard rate'),
        version: this.version,
        is_default: 1
      })
      await this.loadPlans()
    },
    addPlan () {
      this.showDialog({
        title: this._('Add rate plan'),
        buttons: [
          { title: this._('Close'), icon: 'close', handler: () => {} },
          {
            title: this._('Save'),
            class: 'success',
            icon: 'check',
            requiredFields: ['title'],
            handler: async (data) => {
              try {
                const payload = this.planPayload(data)
                const response = await axios.post(this.api('/products/' + this.product.id + '/rateplans.json'), payload)
                const created = this.normalizePlan(response.data)
                await this.loadPlans(created && created.id)
                if (created && created.id && !this.plans.find(p => String(p.id) === String(created.id))) {
                  this.plans = this.plans.concat([created])
                  this.selectedPlan = created
                }
                await this.loadDays()
              } catch (e) {
                if (this.showError) {
                  this.showError(e)
                }
              }
            }
          }
        ],
        fields: this.planFields()
      })
    },
    editPlan (plan) {
      this.showDialog({
        title: this._('Edit rate plan'),
        buttons: [
          { title: this._('Close'), icon: 'close', handler: () => {} },
          ...(this.plans.length > 1 ? [{ title: this._('Delete'), class: 'danger', icon: 'trash', handler: () => this.deletePlan(plan) }] : []),
          {
            title: this._('Save'),
            class: 'success',
            icon: 'check',
            requiredFields: ['title'],
            handler: async (data) => {
              try {
                const payload = this.planPayload(data, plan)
                Object.assign(plan, payload)
                await axios.put(this.api('/products/' + this.product.id + '/rateplans/' + plan.id + '.json'), payload)
                await this.loadPlans(plan.id)
                await this.loadDays()
              } catch (e) {
                if (this.showError) {
                  this.showError(e)
                }
              }
            }
          }
        ],
        fields: this.planFields(plan)
      })
    },
    priceField (field) {
      return Object.assign({
        type: 'number',
        lang: this.numberLocale,
        step: 'any'
      }, field)
    },
    parseDecimal (val) {
      if (val === '' || val === null || val === undefined) {
        return 0
      }
      if (typeof val === 'number') {
        return val
      }
      const n = parseFloat(String(val).replace(',', '.'))
      return isNaN(n) ? 0 : n
    },
    derivedAmountValue (data) {
      const mod = data.derived_modifier || 'increase_by_amount'
      if (String(mod).indexOf('percent') !== -1) {
        return this.parseDecimal(data.derived_percent)
      }
      return this.parseDecimal(data.derived_amount)
    },
    extraNightAmountValue (data) { // One field per mode in the dialog, stored as extra_night_amount
      if (data.extra_night_modifier === 'decrease_by_percent') {
        return this.parseDecimal(data.extra_night_percent)
      }
      if (data.extra_night_modifier === 'decrease_by_amount') {
        return this.parseDecimal(data.extra_night_discount)
      }
      return this.parseDecimal(data.extra_night_amount)
    },
    planFields (plan) {
      const derived = this.derivedOption(plan) || {}
      const derivedIsPercent = String(derived.modifier || '').indexOf('percent') !== -1
      const extraMode = plan && plan.extra_night_modifier ? plan.extra_night_modifier : ''
      const derivedRate = derived.rate || derived.rate === 0 ? derived.rate : ''
      const extraRate = plan && (plan.extra_night_amount || plan.extra_night_amount === 0) ? plan.extra_night_amount : ''
      const base = this.rateplanBaseFields(plan, this.termsOptions, !this.plans.length)
        .filter(field => this.withPlanTerms || (field.name !== 'terms_id' && field.name !== 'allow_cancellation_request'))
      return base.concat([
        {
          title: this._('Rate mode'),
          type: 'select',
          name: 'rate_mode',
          half: 'left',
          defaultValue: plan ? plan.rate_mode : 'manual',
          options: [
            { value: 'manual', name: this._('Manual') },
            { value: 'derived', name: this._('Derived from another plan') }
          ]
        },
        {
          title: this._('Parent rate plan'),
          type: 'select',
          name: 'parent_rateplan_id',
          half: 'right',
          defaultValue: this.defaultParentId(plan),
          condition: 'rate_mode',
          conditionValue: ['derived'],
          options: [{ value: '', name: this._('None') }].concat(this.plans.filter(p => !plan || String(p.id) !== String(plan.id)).map(p => ({ value: p.id, name: p.title })))
        },
        {
          title: this._('Adjustment'),
          type: 'select',
          name: 'derived_modifier',
          half: 'left',
          defaultValue: derived.modifier || 'increase_by_amount',
          condition: 'rate_mode',
          conditionValue: ['derived'],
          options: [
            { value: 'increase_by_amount', name: this._('Increase by amount') },
            { value: 'decrease_by_amount', name: this._('Decrease by amount') },
            { value: 'increase_by_percent', name: this._('Increase by percent') },
            { value: 'decrease_by_percent', name: this._('Decrease by percent') }
          ]
        },
        this.priceField({
          title: this._('Price difference'),
          name: 'derived_amount',
          half: 'right',
          defaultValue: derivedIsPercent ? '' : derivedRate,
          placeholder: '0',
          addon: this.currencyAddon,
          condition: 'rate_mode',
          conditionValue: ['derived'],
          condition2: 'derived_modifier',
          conditionValue2: ['increase_by_amount', 'decrease_by_amount']
        }),
        this.priceField({
          title: this._('Percent difference'),
          name: 'derived_percent',
          half: 'right',
          defaultValue: derivedIsPercent ? derivedRate : '',
          placeholder: '0',
          addon: '%',
          condition: 'rate_mode',
          conditionValue: ['derived'],
          condition2: 'derived_modifier',
          conditionValue2: ['increase_by_percent', 'decrease_by_percent']
        }),
        {
          type: 'info',
          text: this._('Nightly rates and stay restrictions follow the parent plan. You can still override individual dates on the calendar.'),
          condition: 'rate_mode',
          conditionValue: ['derived']
        },
        {
          title: this._('Extra nights'),
          type: 'select',
          name: 'extra_night_modifier',
          half: 'left',
          defaultValue: plan && plan.extra_night_modifier ? plan.extra_night_modifier : 'same', // Not empty: the dialog leaves an empty default unselected
          options: [
            { value: 'same', name: this._('Same as first night') },
            { value: 'decrease_by_amount', name: this._('Decrease by amount') },
            { value: 'decrease_by_percent', name: this._('Decrease by percent') },
            { value: 'set_rate', name: this._('Set extra night rate') }
          ]
        },
        this.priceField({
          title: this._('Discount per night'),
          name: 'extra_night_discount',
          half: 'right',
          defaultValue: extraMode === 'decrease_by_amount' ? extraRate : '',
          placeholder: '0',
          addon: this.currencyAddon,
          condition: 'extra_night_modifier',
          conditionValue: ['decrease_by_amount'],
          help: this._('Subtracted from the price of the second and later nights.')
        }),
        this.priceField({
          title: this._('Discount per night'),
          name: 'extra_night_percent',
          half: 'right',
          defaultValue: extraMode === 'decrease_by_percent' ? extraRate : '',
          placeholder: '0',
          addon: '%',
          condition: 'extra_night_modifier',
          conditionValue: ['decrease_by_percent'],
          help: this._('Subtracted from the price of the second and later nights.')
        }),
        this.priceField({
          title: this._('Extra night price'),
          name: 'extra_night_amount',
          half: 'right',
          defaultValue: extraMode === 'set_rate' ? extraRate : '',
          placeholder: '0',
          addon: this.currencyAddon,
          condition: 'extra_night_modifier',
          conditionValue: ['set_rate'],
          help: this._('The price of the second and later nights.')
        }),
        ...this.guestFields(plan),
        {
          type: 'info',
          text: this._('Rate plans use this product\'s VAT. Breakfast or other extras with a different VAT should be sold as a separate product.')
        }
      ])
    },
    planTierFees (plan) { // rateplan.pricetiers: {tier id: price per extra guest and night}
      return this.tierMap(plan && plan.pricetiers)
    },
    planTierPercents (plan) { // rateplan.pricetiers_percent: {tier id: percentage of the night price per extra guest}
      return this.tierMap(plan && plan.pricetiers_percent)
    },
    planIsPerPerson (plan) {
      return !!(plan && this.adultTierId && parseInt(plan.includes) === 1 && Number(this.planTierPercents(plan)[this.adultTierId]) === 100)
    },
    tierMap (raw) {
      if (!raw) {
        return {}
      }
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
      } catch (e) {
        return {}
      }
    },
    guestFields (plan) { // Guests in the base price, then a price per extra guest for each price tier
      if (!this.tierList.length) {
        return [{ type: 'info', text: this._('Add price tiers to the product to charge for extra guests.') }]
      }
      const fees = this.planTierFees(plan)
      const percents = this.planTierPercents(plan)
      const has = (map, id) => map[id] !== undefined && map[id] !== null
      const fields = [{
        title: this._('Base price includes'),
        type: 'number',
        name: 'includes',
        defaultValue: plan && parseInt(plan.includes) > 0 ? String(parseInt(plan.includes)) : '',
        placeholder: '0',
        addon: this._('guests'),
        help: this._('Further guests pay the price of their price tier, adults first. For a price per person: 1 guest, and adults at 100 percent of the night price.')
      }]
      this.tierList.forEach(tier => { // Each price tier: free, a fixed price, or a percentage of the night price
        const mode = has(percents, tier.id) ? 'percent' : (has(fees, tier.id) ? 'amount' : 'free') // Not empty: the dialog leaves an empty default unselected
        fields.push({
          title: this._('%s / night').replace('%s', tier.name),
          type: 'select',
          name: 'tiermode_' + tier.id,
          half: 'left',
          defaultValue: mode,
          options: [
            { value: 'free', name: this._('Free') },
            { value: 'amount', name: this._('Fixed price') },
            { value: 'percent', name: this._('Percentage of the night price') }
          ]
        })
        fields.push(this.priceField({
          name: 'tier_' + tier.id,
          half: 'right',
          defaultValue: mode === 'amount' ? String(fees[tier.id]) : '',
          placeholder: '0',
          addon: this.currencyAddon,
          condition: 'tiermode_' + tier.id,
          conditionValue: ['amount']
        }))
        fields.push(this.priceField({
          name: 'tierpct_' + tier.id,
          half: 'right',
          defaultValue: mode === 'percent' ? String(percents[tier.id]) : '',
          placeholder: '100',
          addon: '%',
          condition: 'tiermode_' + tier.id,
          conditionValue: ['percent']
        }))
      })
      return fields
    },
    guestPayload (data, plan) {
      const fees = Object.assign({}, this.planTierFees(plan)) // Keeps the prices of price tiers no longer on the product
      const percents = Object.assign({}, this.planTierPercents(plan))
      const filled = value => value !== '' && value !== null && value !== undefined
      this.tierList.forEach(tier => {
        const mode = data['tiermode_' + tier.id]
        delete fees[tier.id]
        delete percents[tier.id]
        if (mode === 'amount' && filled(data['tier_' + tier.id])) {
          fees[tier.id] = this.parseDecimal(data['tier_' + tier.id])
        } else if (mode === 'percent' && filled(data['tierpct_' + tier.id])) {
          percents[tier.id] = this.parseDecimal(data['tierpct_' + tier.id])
        }
      })
      const includes = parseInt(data.includes)
      return {
        includes: includes > 0 ? includes : null,
        pricetiers: fees,
        pricetiers_percent: percents
      }
    },
    planPayload (data, plan) {
      const derived = data.rate_mode === 'derived'
      const kept = this.planOptions(plan).filter(opt => opt && !opt.derived_option)
      const derivedAmount = this.derivedAmountValue(data)
      const options = derived
        ? [{ occupancy: 0, derived_option: { modifier: data.derived_modifier || 'increase_by_amount', rate: derivedAmount } }].concat(kept)
        : kept
      const base = this.rateplanBasePayload(data)
      if (!this.withPlanTerms) {
        delete base.terms_id
        delete base.allow_cancellation_request
      }
      if (!base.is_default && plan && parseInt(plan.is_default)) { // One default is always kept, so it moves by setting another plan as the default
        delete base.is_default
      }
      return Object.assign(base, {
        rate_mode: derived ? 'derived' : 'manual',
        parent_rateplan_id: derived ? (data.parent_rateplan_id || null) : null,
        inherit_rate: derived ? 1 : 0,
        options,
        derived_modifier: derived ? (data.derived_modifier || 'increase_by_amount') : null,
        derived_amount: derived ? derivedAmount : 0,
        extra_night_modifier: data.extra_night_modifier && data.extra_night_modifier !== 'same' ? data.extra_night_modifier : null,
        extra_night_amount: data.extra_night_modifier && data.extra_night_modifier !== 'same' ? this.extraNightAmountValue(data) : 0
      }, this.guestPayload(data, plan))
    },
    async setDefault (plan) {
      await axios.put(this.api('/products/' + this.product.id + '/rateplans/' + plan.id + '.json'), { is_default: 1 })
      await this.loadPlans()
    },
    async deletePlan (plan) {
      if (!confirm(this._('Delete this rate plan?'))) {
        return
      }
      await axios.delete(this.api('/products/' + this.product.id + '/rateplans/' + plan.id + '.json'))
      this.selectedPlan = null
      await this.loadPlans()
      await this.loadDays()
    },
    async applyRange (clear) {
      if (!this.selectedPlan) {
        await this.ensureDefaultPlan()
      }
      if (!this.selectedPlan || !this.rangeFrom || !this.rangeTo) {
        return
      }
      const value = {
        date_from: this.rangeFrom,
        date_to: this.rangeTo
      }
      if (this.rangeDays.length && this.rangeDays.length < 7) {
        value.days = this.rangeDays.slice()
      }
      if (clear) { // Back to the weekly defaults
        if (!confirm(this._('Remove the date changes of the selected dates?'))) {
          return
        }
        value.clear = 1
        await this.postRestrictions(value)
        return
      }
      if (this.formChanged('rate') && this.form.rate !== '' && this.form.rate !== null) {
        value.rate = this.parseDecimal(this.form.rate)
      }
      ;['min_stay_arrival', 'min_stay_through', 'max_stay'].forEach(field => {
        if (this.formChanged(field) && this.form[field] !== '' && this.form[field] !== null) {
          value[field] = this.form[field]
        }
      })
      ;['stop_sell', 'closed_to_arrival', 'closed_to_departure'].forEach(field => {
        if (this.formChanged(field)) {
          this.assignRestriction(value, field, this.form[field])
        }
      })
      const rates = this.occupancyRates.filter(r => r.occupancy && r.rate !== '' && r.rate !== null)
      if (rates.length) {
        value.rates = rates.map(r => ({ occupancy: Number(r.occupancy), rate: this.parseDecimal(r.rate) }))
      }
      if (!Object.keys(value).some(key => !['date_from', 'date_to', 'days'].includes(key))) {
        this.showError(this._('Nothing was changed.'))
        return
      }
      await this.postRestrictions(value)
    },
    async postRestrictions (value) {
      this.applying = true
      const prev = JSON.parse(JSON.stringify(this.days))
      if (!value.clear) {
        this.applyOptimistic(value)
      }
      try {
        await axios.post(this.api('/products/' + this.product.id + '/rateplans/' + this.selectedPlan.id + '/restrictions.json'), { values: [value] })
        this.appliedHint = this._('Rates updated')
        setTimeout(() => { this.appliedHint = '' }, 2000)
        await this.loadDays()
        if (this.$refs.shell) {
          this.$refs.shell.closePanel()
        }
      } catch (e) {
        this.days = prev
        if (this.showError) {
          this.showError(e)
        }
      }
      this.applying = false
    },
    assignRestriction (value, field, raw) {
      if (raw === '1' || raw === 1 || raw === true) {
        value[field] = true
        return
      }
      if (raw === '0' || raw === 0 || raw === false) {
        value[field] = false
      }
    },
    applyOptimistic (value) {
      const from = new Date(value.date_from)
      const to = new Date(value.date_to)
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const iso = this.toIso(d)
        const wd = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'][d.getDay()]
        if (value.days && value.days.length && value.days.indexOf(wd) === -1) {
          continue
        }
        const night = Object.assign({}, this.days[iso] || {})
        if (value.rate !== undefined) {
          night.rate = value.rate
        }
        if (value.min_stay_arrival !== undefined) {
          night.min_stay_arrival = value.min_stay_arrival
        }
        if (value.min_stay_through !== undefined) {
          night.min_stay_through = value.min_stay_through
        }
        if (value.max_stay !== undefined) {
          night.max_stay = value.max_stay
        }
        if (value.stop_sell !== undefined) {
          night.stop_sell = value.stop_sell
        }
        if (value.closed_to_arrival !== undefined) {
          night.closed_to_arrival = value.closed_to_arrival
        }
        if (value.closed_to_departure !== undefined) {
          night.closed_to_departure = value.closed_to_departure
        }
        this.days[iso] = night
      }
    }
  },
  beforeUnmount () {
    window.removeEventListener('pointerup', this.weekPointerUp)
    window.removeEventListener('pointercancel', this.weekPointerCancel)
  },
  async mounted () {
    if (!this.canLoadRates) {
      return
    }
    await this.reload()
    if (!this.plans.length) {
      await this.ensureDefaultPlan()
      await this.loadDays()
    }
  }
}
</script>

<style scoped>
.derived-rate {
  font-style: italic
}
.derived-plan-hint,
.applied-hint {
  margin-top: 0;
  margin-bottom: 8px
}
.calendar-weekly-grid { /* Drawn like one week of the calendar */
  margin-bottom: 0;
  table-layout: fixed
}
.calendar-weekly-grid td.weekly-cell {
  cursor: pointer;
  min-width: 64px;
  height: 84px;
  vertical-align: top;
  padding: 4px
}
.calendar-weekly-grid th {
  font-weight: 600;
  color: #555
}
.calendar-weekly-grid .weekly-weekend {
  background-color: #fafafa
}
.calendar-weekly-grid th.weekly-head {
  cursor: pointer;
  user-select: none
}
.calendar-weekly-grid th.weekly-head:hover {
  background-color: #eef3f8
}
.calendar-weekly-grid td.weekly-selected { /* Weekdays being selected or edited together */
  background-color: #f2f7fd;
  box-shadow: inset 0 0 0 2px #337ab7
}
.calendar-weekly-grid td.weekly-closed {
  box-shadow: inset 0 3px 0 #d9534f
}
.weekly-label {
  display: none
}
.night-marks {
  display: flex;
  flex-wrap: wrap;
  gap: 0 6px;
  margin-top: 2px;
  font-size: 11px;
  color: #777
}
.calendar-weekly-grid td.weekly-cell:hover,
.calendar-weekly-grid td.weekly-cell:focus {
  outline: 2px solid #337ab7;
  outline-offset: -2px
}
.calendar-weekly-grid .every-col {
  background-color: #f9f9f9
}
.calendar-weekly-grid td.danger {
  background-color: #f2dede
}
/* Narrow: the week as a list of weekdays */
@container pricing-calendar (max-width: 680px) {
  .calendar-weekly-grid,
  .calendar-weekly-grid tbody,
  .calendar-weekly-grid tr,
  .calendar-weekly-grid td {
    display: block;
    width: 100%
  }
  .calendar-weekly-grid {
    border: 0
  }
  .calendar-weekly-grid thead {
    display: none
  }
  .calendar-weekly-grid > tbody > tr > td.weekly-cell {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 12px;
    height: auto;
    padding: 10px 6px;
    border-width: 0 0 1px 0
  }
  .weekly-label {
    display: block;
    flex: 0 0 72px;
    font-weight: 600
  }
}
.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 16px;
  margin-top: 6px;
  margin-bottom: 16px
}
</style>
